import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { MovieRow } from '../components/MovieRow';
import { MovieCard } from '../components/MovieCard';
import { filmsApi, watchProgressApi, analyticsApi, formatDuration, thumbUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import CreatorProfileModal from '../components/CreatorProfileModal';
import AdBannerPair from '../components/AdBannerPair';
import AdSenseBanner, { AD_SLOTS } from '../components/AdSenseBanner';
import AdsterraBanner from '../components/AdsterraBanner';
import AdsterraNativeBanner from '../components/AdsterraNativeBanner';
import PopunderScript from '../components/PopunderScript';
import { TrendingUp, Film, Tv, Frown, Sparkles, Star, Play, Info, ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import { toTitleCase } from '../lib/utils';

/* ── Coming Soon Card (rectangular, matching MovieCard style) ── */
const ComingSoonCard = ({ film }) => {
  const navigate = useNavigate();
  const releaseDate = film.release_date ? new Date(film.release_date + 'T00:00:00') : null;
  return (
    <div className="movie-card relative rounded-sm overflow-hidden cursor-pointer group"
      style={{ width: '280px', minWidth: '280px' }}
      onClick={() => navigate(`/film/${film.film_id}`)}
      data-testid={`coming-soon-card-${film.film_id}`}>
      <img src={thumbUrl(film.thumbnail)} alt={film.title} className="movie-thumbnail" loading="lazy" />
      {/* Coming Soon badge */}
      <div className="absolute top-2 left-2 z-20 flex rounded overflow-hidden text-[11px] font-bold" data-testid="coming-soon-badge">
        <span className="px-2 py-1 bg-white text-black">Coming</span>
        <span className="px-2 py-1 bg-[#8B5CF6] text-white">Soon</span>
      </div>
      {/* Bottom gradient with date */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-8">
        <h3 className="text-sm font-semibold text-white line-clamp-1">{toTitleCase(film.title)}</h3>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-xs text-gray-400">{film.creator}</p>
          {releaseDate && (
            <span className="flex items-center gap-1 text-xs text-gray-300">
              <Calendar className="w-3 h-3" />
              {releaseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
          <Play className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
};

/* ── Coming Soon Section (split into Movies & Series rows) ── */
const ComingSoonSection = ({ movies, series }) => {
  const allContent = [...(movies || []), ...(series || [])];
  if (allContent.length === 0) return null;
  return (
    <div className="space-y-6" data-testid="coming-soon-section">
      <h2 className="category-title flex items-center gap-3">
        <Clock className="w-5 h-5 text-[#8B5CF6]" />
        Coming Soon
      </h2>

      {movies && movies.length > 0 && (
        <div data-testid="coming-soon-movies">
          <div className="flex items-center gap-2 mb-3">
            <Film className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-300">Movies</h3>
          </div>
          <div className="scroll-rail hide-scrollbar">
            {movies.map(film => <ComingSoonCard key={film.film_id} film={film} />)}
          </div>
        </div>
      )}

      {series && series.length > 0 && (
        <div data-testid="coming-soon-series">
          <div className="flex items-center gap-2 mb-3">
            <Tv className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-300">Series</h3>
          </div>
          <div className="scroll-rail hide-scrollbar">
            {series.map(s => <ComingSoonCard key={s.film_id} film={s} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const Top10Row = ({ films }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll, films]);

  if (!films || films.length === 0) return null;

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = direction === 'left' ? -400 : 400;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  // Allow horizontal scrolling via mouse wheel
  const handleWheel = (e) => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    if (el.scrollWidth <= el.clientWidth) return;
    // Convert vertical scroll to horizontal when no horizontal delta
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.deltaX === 0) {
      e.preventDefault();
      el.scrollBy({ left: e.deltaY, behavior: 'auto' });
    } else if (Math.abs(e.deltaX) > 0) {
      e.preventDefault();
      el.scrollBy({ left: e.deltaX, behavior: 'auto' });
    }
  };

  return (
    <div data-testid="top-10-section">
      <div className="flex items-center gap-3 mb-4">
        <TrendingUp className="w-6 h-6 text-[#00FF9D]" />
        <h2
          className="text-xl md:text-2xl font-bold tracking-tight"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Top 10 This Week
        </h2>
        <span className="px-2 py-0.5 text-xs font-bold rounded bg-red-600 text-white">
          HOT
        </span>
      </div>
      <div className="relative group">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-full bg-gradient-to-r from-[#030014] to-transparent opacity-70 hover:opacity-100 transition-opacity flex items-center justify-start"
            data-testid="top10-scroll-left"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}

        <div
          ref={scrollRef}
          onWheel={handleWheel}
          onScroll={checkScroll}
          className="flex gap-6 overflow-x-auto pb-4 hide-scrollbar"
          style={{ overscrollBehaviorX: 'contain', scrollBehavior: 'auto' }}
        >
          {films.map((film) => (
            <Top10Card key={film.film_id} film={film} />
          ))}
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-full bg-gradient-to-l from-[#030014] to-transparent opacity-70 hover:opacity-100 transition-opacity flex items-center justify-end"
            data-testid="top10-scroll-right"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        )}
      </div>
    </div>
  );
};

const Top10Card = ({ film }) => {
  const navigate = useNavigate();
  return (
    <div
      className="shrink-0 cursor-pointer group/card"
      style={{ width: film.rank >= 10 ? '300px' : '220px' }}
      onClick={() => navigate(`/film/${film.film_id}`)}
      data-testid={`top10-card-${film.film_id}`}
    >
      {/* Card with number behind and thumbnail on top */}
      <div className="relative overflow-hidden" style={{ height: '200px' }}>
        {/* Rank number — behind thumbnail, shifted left to be more visible */}
        <span
          className="absolute bottom-0 z-[1] select-none leading-none"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '150px',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '3px rgba(139, 92, 246, 0.55)',
            filter: 'drop-shadow(0 0 30px rgba(139, 92, 246, 0.2))',
            left: '4px',
          }}
        >
          {film.rank}
        </span>
        {/* Thumbnail — positioned to the right, on top of number */}
        <div className="absolute top-0 right-0 z-[2] w-[140px] h-[200px] rounded-sm overflow-hidden">
          <img
            src={thumbUrl(film.thumbnail, 240)}
            alt={film.title}
            className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {/* Hover play overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/card:opacity-100 transition-opacity flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Play className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        {/* Title overlay — on top of both number and thumbnail */}
        <div className="absolute bottom-0 left-0 right-0 z-[3] px-2 pb-2 pt-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none">
          <p className="text-sm font-bold text-white uppercase line-clamp-2 leading-tight tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid={`top10-title-${film.film_id}`}>
            {toTitleCase(film.title)}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Category Card (shows title + rating below thumbnail) ── */
const CategoryFilmCard = ({ film }) => {
  const navigate = useNavigate();
  return (
    <div className="group cursor-pointer" onClick={() => navigate(`/film/${film.film_id}`)}
      data-testid={`category-card-${film.film_id}`}>
      <div className="relative rounded-sm overflow-hidden aspect-video mb-2">
        <img src={thumbUrl(film.thumbnail)} alt={film.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
          <p className="text-xs text-white font-medium line-clamp-1">{toTitleCase(film.title)}</p>
        </div>
        {film.is_premium_content && (
          <div className="absolute top-2 right-2 bg-yellow-500/90 text-black px-2 py-0.5 rounded text-xs font-bold">
            ${film.premium_price?.toFixed(2)}
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-white line-clamp-1 group-hover:text-[#8B5CF6] transition-colors"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {toTitleCase(film.title)}
      </h3>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="flex items-center gap-1 text-xs text-[#8B5CF6]">
          <Star className="w-3 h-3 fill-current" /> {film.avg_rating?.toFixed(1) || '0.0'}
        </span>
        <span className="text-xs text-gray-500">{film.year}</span>
      </div>
    </div>
  );
};

const HomePage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Derive content type from path or query params
  const pathType = location.pathname === '/browse/films'
    ? 'film'
    : location.pathname === '/browse/series'
      ? 'series'
      : null;
  const contentTypeFilter = pathType || searchParams.get('type');
  const genreFilter = searchParams.get('genre');
  const sectionFilter = searchParams.get('section');

  const [featuredFilms, setFeaturedFilms] = useState([]);
  const [filmsByCategory, setFilmsByCategory] = useState({});
  const [comingSoon, setComingSoon] = useState({ movies: [], series: [] });
  const [continueWatching, setContinueWatching] = useState([]);
  const [top10, setTop10] = useState([]);
  const [filteredFilms, setFilteredFilms] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const { isAuthenticated } = useAuth();
  const abortRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Cancel previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchData = async () => {
      setLoading(true);
      try {
        const isHomePage = !contentTypeFilter && !genreFilter;

        let featured, allFilms, top10Data, comingSoonData;

        if (isHomePage) {
          // Single aggregated call for homepage — much faster
          const data = await filmsApi.getHomepageData();
          featured = data.featured || [];
          allFilms = data.allFilms || [];
          top10Data = data.top10 || [];
          comingSoonData = data.comingSoon || { movies: [], series: [] };
        } else {
          // Filtered views still use individual endpoints
          const params = { limit: 100 };
          if (contentTypeFilter) params.content_type = contentTypeFilter;
          if (genreFilter) params.genre = genreFilter;
          [featured, allFilms, top10Data] = await Promise.all([
            filmsApi.getFeatured(),
            filmsApi.getAll(params),
            filmsApi.getTop10Weekly(),
          ]);
          comingSoonData = null;
        }

        // Check if request was aborted
        if (controller.signal.aborted) return;

        setFeaturedFilms(featured);
        setTop10(top10Data);
        if (comingSoonData) setComingSoon(comingSoonData);

        // If filtering, show as a flat grid
        if (contentTypeFilter || genreFilter) {
          setFilteredFilms(allFilms);
          setFilmsByCategory({});
        } else {
          setFilteredFilms([]);
          const categories = {};
          const categoryOrder = [
            'New Arrivals', 'Popular',
            'Horror', 'Documentary', 'Comedy',
          ];
          
          categoryOrder.forEach((cat) => {
            const catFilms = allFilms.filter((f) => f.categories?.includes(cat));
            if (catFilms.length > 0) categories[cat] = catFilms;
          });

          const topRated = [...allFilms].sort((a, b) => b.avg_rating - a.avg_rating).slice(0, 10);
          if (topRated.length > 0) categories['Top Rated'] = topRated;

          const seriesContent = allFilms.filter((f) => f.content_type === 'series');
          if (seriesContent.length > 0) categories['Series'] = seriesContent;

          // Genre-based carousels: add genres with >= 5 items
          const genreCounts = {};
          allFilms.forEach((f) => {
            (f.genres || []).forEach((g) => {
              if (!genreCounts[g]) genreCounts[g] = [];
              genreCounts[g].push(f);
            });
          });
          Object.entries(genreCounts)
            .filter(([, films]) => films.length >= 5)
            .sort((a, b) => b[1].length - a[1].length)
            .forEach(([genre, films]) => {
              if (!categories[genre]) categories[genre] = films;
            });

          setFilmsByCategory(categories);
        }

        // Fetch secondary data in parallel (non-blocking)
        const secondaryPromises = [];
        if (!comingSoonData) {
          secondaryPromises.push(
            filmsApi.getComingSoon().then(d => { if (!controller.signal.aborted) setComingSoon(d); }).catch(() => {}),
          );
        }
        if (isAuthenticated) {
          secondaryPromises.push(
            watchProgressApi.getContinueWatching().then(d => { if (!controller.signal.aborted) setContinueWatching(d); }).catch(() => {}),
            analyticsApi.getRecommendations().then(d => { if (!controller.signal.aborted) setRecommendations(d?.recommendations || []); }).catch(() => {}),
          );
        }
        await Promise.all(secondaryPromises);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to fetch films:', error);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, [isAuthenticated, contentTypeFilter, genreFilter, sectionFilter]);

  const isHomePage = !contentTypeFilter && !genreFilter && !sectionFilter;

  const filterLabel = contentTypeFilter
    ? `${contentTypeFilter === 'series' ? 'Series' : 'Movies'}${genreFilter ? ` - ${genreFilter}` : ''}`
    : genreFilter
      ? genreFilter
      : null;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="home-page">
      <PopunderScript />
      <Navbar />

      {/* Hero Section - homepage uses all featured, category pages use filtered featured */}
      {isHomePage && featuredFilms.length > 0 && (
        <HeroSection films={featuredFilms} onCreatorClick={setSelectedCreator} />
      )}
      {!isHomePage && !loading && filteredFilms.length >= 1 && (
        <HeroSection
          films={[...filteredFilms].sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0)).slice(0, 5)}
          onCreatorClick={setSelectedCreator}
        />
      )}

      {/* Content Sections */}
      <div className={`relative z-10 pb-20 ${(isHomePage || (!loading && filteredFilms.length >= 1)) ? '-mt-8' : 'pt-24'}`}>
        <div className="w-full px-4 md:px-8 lg:px-[4%] space-y-8">

          {/* ADBANNER1 (Adsterra): Between Hero and content */}
          <AdBannerPair />
          {/* Filter Header */}
          {filterLabel && (
            <div className="flex items-center gap-3" data-testid="filter-header">
              {contentTypeFilter === 'series' ? (
                <Tv className="w-7 h-7 text-[#00FF9D]" />
              ) : (
                <Film className="w-7 h-7 text-[#8B5CF6]" />
              )}
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {filterLabel}
              </h1>
              {!loading && (
                <span className="text-gray-400 text-sm" data-testid="filter-count">
                  ({filteredFilms.length} {filteredFilms.length === 1 ? 'title' : 'titles'})
                </span>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && filterLabel && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton aspect-video rounded-sm" />
              ))}
            </div>
          )}

          {/* Top 10 Section */}
          {(sectionFilter === 'top10' || isHomePage) && !loading && (
            <Top10Row films={top10} />
          )}

          {/* Filtered Grid - using MovieCard for uniform thumbnails */}
          {!loading && filteredFilms.length > 0 && (
            <div className="flex flex-wrap gap-4" data-testid="filtered-grid">
              {filteredFilms.map((film) => (
                <MovieCard key={film.film_id} film={film} />
              ))}
            </div>
          )}

          {/* Continue Watching */}
          {isHomePage && isAuthenticated && continueWatching.length > 0 && (
            <MovieRow
              title="Continue Watching"
              films={continueWatching}
              loading={loading}
            />
          )}

          {/* Recommended For You */}
          {isHomePage && isAuthenticated && recommendations.length > 0 && (
            <div data-testid="recommendations-section">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
                <h2 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Recommended For You
                </h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {recommendations.map((film) => (
                  <MovieCard key={film.film_id} film={film} />
                ))}
              </div>
            </div>
          )}

          {/* Category Rows with Coming Soon between New Arrivals and Popular */}
          {!loading && (() => {
            const entries = Object.entries(filmsByCategory);
            const newArrivals = entries.filter(([cat]) => cat === 'New Arrivals');
            const rest = entries.filter(([cat]) => cat !== 'New Arrivals');
            const allRows = [...newArrivals, ...rest];
            const midpoint = Math.ceil(allRows.length / 2);
            return (
              <>
                {newArrivals.map(([category, films]) => (
                  <MovieRow key={category} title={category} films={films} loading={loading} />
                ))}

                {/* Coming Soon between New Arrivals and rest */}
                {isHomePage && (comingSoon.movies?.length > 0 || comingSoon.series?.length > 0) && (
                  <ComingSoonSection movies={comingSoon.movies} series={comingSoon.series} />
                )}

                {rest.map(([category, films], idx) => (
                  <React.Fragment key={category}>
                    {/* ADBANNER3 (Adsterra 728x90): Middle of category carousels — desktop only */}
                    {isHomePage && !isMobile && idx === midpoint - newArrivals.length - 1 && (
                      <AdsterraBanner adKey="0ea99be9983759dc310b0a31284a9a71" width={728} height={90} testId="ad-banner-3" />
                    )}
                    <MovieRow title={category} films={films} loading={loading} />
                  </React.Fragment>
                ))}
              </>
            );
          })()}

          {/* ADBANNER4 (Adsterra Native): Before footer */}
          <AdsterraNativeBanner testId="ad-banner-4" />

          {/* Empty state for filters */}
          {!loading && filterLabel && filteredFilms.length === 0 && (
            <div className="text-center py-20" data-testid="empty-state">
              <Frown className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg mb-2">
                No {contentTypeFilter === 'series' ? 'series' : 'films'} available
                {genreFilter && ` in ${genreFilter}`}
              </p>
              <p className="text-gray-500 text-sm">
                {contentTypeFilter === 'series'
                  ? 'Series content is coming soon. Check back later!'
                  : 'Try browsing a different category or check back later.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedCreator && (
        <CreatorProfileModal
          creatorName={selectedCreator}
          onClose={() => setSelectedCreator(null)}
        />
      )}
    </div>
  );
};

export default HomePage;
